import { Check } from 'lucide-react';
import { Checkbox as C, Label as L, RadioGroup as R, Switch as S } from 'radix-ui';
import {
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from 'react';
import { cn } from '../lib/cn';

const control =
  'w-full rounded-md border border-line-strong bg-surface text-ink placeholder:text-ink-subtle ' +
  'transition-colors duration-150 hover:border-ink-subtle focus-visible:border-primary ' +
  'focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-primary/30 ' +
  'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-subtle ' +
  'aria-invalid:border-danger aria-invalid:focus-visible:outline-danger/30';

export function Input({
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  return <input className={cn(control, 'h-10 px-3 text-sm', className)} {...rest} />;
}

export function Textarea({
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { ref?: Ref<HTMLTextAreaElement> }) {
  return <textarea className={cn(control, 'min-h-24 px-3 py-2 text-sm', className)} {...rest} />;
}

/** Native select: best keyboard, screen-reader and mobile behavior, RTL-correct for free. */
export function Select({
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { ref?: Ref<HTMLSelectElement> }) {
  return (
    <select className={cn(control, 'h-10 pe-8 ps-3 text-sm', className)} {...rest}>
      {children}
    </select>
  );
}

export function Label(props: { htmlFor?: string; children: ReactNode; className?: string }) {
  return (
    <L.Root htmlFor={props.htmlFor} className={cn('text-sm font-medium text-ink', props.className)}>
      {props.children}
    </L.Root>
  );
}

export interface FieldControlProps {
  readonly id: string;
  readonly 'aria-invalid': boolean | undefined;
  readonly 'aria-describedby': string | undefined;
  readonly 'aria-required': boolean | undefined;
}

/**
 * Label + control + help text + error, wired for assistive tech: the control gets its id,
 * `aria-invalid`, `aria-required` and `aria-describedby` pointing at hint and error.
 */
export function FormField(props: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Visually marks required fields; the text comes from the app (e.g. "required"). */
  requiredLabel?: string;
  className?: string;
  children: (control: FieldControlProps) => ReactNode;
}) {
  const id = useId();
  const hintId = props.hint ? `${id}-hint` : undefined;
  const errorId = props.error ? `${id}-error` : undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', props.className)}>
      <Label htmlFor={id}>
        {props.label}
        {props.required ? (
          <span className="ms-1 text-danger" aria-hidden="true" title={props.requiredLabel}>
            *
          </span>
        ) : null}
      </Label>
      {props.children({
        id,
        'aria-invalid': props.error ? true : undefined,
        'aria-describedby': [hintId, errorId].filter(Boolean).join(' ') || undefined,
        'aria-required': props.required || undefined,
      })}
      {props.hint ? (
        <p id={hintId} className="text-xs text-ink-muted">
          {props.hint}
        </p>
      ) : null}
      {props.error ? (
        <p id={errorId} className="text-xs font-medium text-danger">
          {props.error}
        </p>
      ) : null}
    </div>
  );
}

export function Checkbox(props: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
}) {
  const generated = useId();
  const id = props.id ?? generated;
  return (
    <div className="flex items-start gap-3">
      <C.Root
        id={id}
        checked={props.checked}
        disabled={props.disabled}
        onCheckedChange={(v) => props.onCheckedChange(v === true)}
        className={cn(
          'mt-0.5 flex size-4.5 shrink-0 items-center justify-center rounded-sm border border-line-strong bg-surface',
          'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-on-primary',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <C.Indicator>
          <Check className="size-3.5" strokeWidth={3} aria-hidden="true" />
        </C.Indicator>
      </C.Root>
      <div className="flex flex-col">
        <label htmlFor={id} className="text-sm text-ink">
          {props.label}
        </label>
        {props.description ? (
          <span className="text-xs text-ink-muted">{props.description}</span>
        ) : null}
      </div>
    </div>
  );
}

export function Switch(props: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-4">
      <label htmlFor={id} className="text-sm text-ink">
        {props.label}
      </label>
      <S.Root
        id={id}
        checked={props.checked}
        disabled={props.disabled}
        onCheckedChange={props.onCheckedChange}
        className="relative h-5.5 w-10 shrink-0 rounded-full bg-line-strong transition-colors duration-150 data-[state=checked]:bg-primary disabled:opacity-50"
      >
        <S.Thumb className="block size-4.5 translate-x-0.5 rounded-full bg-white shadow transition-transform duration-150 data-[state=checked]:translate-x-5 rtl:-translate-x-0.5 rtl:data-[state=checked]:-translate-x-5" />
      </S.Root>
    </div>
  );
}

export function RadioGroup(props: {
  value: string;
  onValueChange: (value: string) => void;
  options: readonly { value: string; label: ReactNode; description?: ReactNode }[];
  label: string;
}) {
  return (
    <R.Root
      aria-label={props.label}
      value={props.value}
      onValueChange={props.onValueChange}
      className="flex flex-col gap-2"
    >
      {props.options.map((option) => (
        // biome-ignore lint/a11y/noLabelWithoutControl: the Radix radio item inside is the control
        <label key={option.value} className="flex cursor-pointer items-start gap-3">
          <R.Item
            value={option.value}
            className="mt-0.5 flex size-4.5 shrink-0 items-center justify-center rounded-full border border-line-strong bg-surface data-[state=checked]:border-primary"
          >
            <R.Indicator className="size-2.5 rounded-full bg-primary" />
          </R.Item>
          <span className="flex flex-col">
            <span className="text-sm text-ink">{option.label}</span>
            {option.description ? (
              <span className="text-xs text-ink-muted">{option.description}</span>
            ) : null}
          </span>
        </label>
      ))}
    </R.Root>
  );
}
